/**
 * Picking list (§12.2, Task 4) — one printable A4 document per batch, in two
 * selectable layouts (default consolidated):
 *   • Consolidated-by-SKU: quantities aggregated across the batch, grouped/sorted
 *     by bin location for an efficient walk.
 *   • Per-order (the EasyScan example): header = company + Location + timestamp;
 *     columns Item · SKU · Bin · Per order (#order × qty) · Required; footer
 *     "Total items required: N".
 * Bin locations are used when present (SkuLocation; "—" otherwise).
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, type PickingListData, type PickingListLayout } from "../../lib/api.js";
import { Button } from "../../components/ui/index.js";
import { Thumb } from "../../components/ui/Thumb.js";
import { PrintFrame } from "./PrintFrame.js";
import { DocBrand, fmtDateTime } from "./docBits.js";

function warehouseLine(w: PickingListData["warehouse"]): string {
  return [w.company ?? w.name, w.street1, [w.postcode, w.city].filter(Boolean).join(" "), w.country]
    .filter(Boolean)
    .join(", ");
}

export function PickingList() {
  const { batchId } = useParams();
  const [params, setParams] = useSearchParams();
  const layout: PickingListLayout = params.get("layout") === "per-order" ? "per-order" : "consolidated";
  const [data, setData] = useState<PickingListData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    if (!batchId) return;
    setData(null);
    api
      .getPickingList(batchId, layout)
      .then((d) => live && setData(d))
      .catch(() => live && setError("Could not load this picking list."));
    return () => {
      live = false;
    };
  }, [batchId, layout]);

  const switcher = (
    <div className="doc-toolbar__switch" role="group" aria-label="Layout">
      <Button
        size="sm"
        variant={layout === "consolidated" ? "primary" : "ghost"}
        onClick={() => setParams({ layout: "consolidated" })}
      >
        Consolidated
      </Button>
      <Button
        size="sm"
        variant={layout === "per-order" ? "primary" : "ghost"}
        onClick={() => setParams({ layout: "per-order" })}
      >
        Per order
      </Button>
    </div>
  );

  if (error) {
    return (
      <PrintFrame title="Picking list" actions={switcher}>
        <div className="doc">
          <p>{error}</p>
        </div>
      </PrintFrame>
    );
  }
  if (!data) {
    return (
      <PrintFrame title="Picking list" actions={switcher}>
        <div className="doc">
          <p>Loading…</p>
        </div>
      </PrintFrame>
    );
  }

  return (
    <PrintFrame title={`Picking list · ${data.batchId}`} actions={switcher}>
      <article className="doc" aria-label={`Picking list for ${data.batchId}`}>
        <header className="doc-head">
          <DocBrand subtitle={layout === "per-order" ? "Picking list · per order" : "Picking list · consolidated"} />
          <div className="doc-head__right">
            <span className="doc-title">Picking list</span>
            <span className="doc-meta">
              <strong>Location:</strong> {warehouseLine(data.warehouse)}
            </span>
            <span className="doc-meta">{fmtDateTime(data.generatedAt)}</span>
            <span className="doc-meta mono">
              {data.batchId} · {data.orderCount} order{data.orderCount === 1 ? "" : "s"}
            </span>
          </div>
        </header>

        {layout === "consolidated" ? (
          <table className="doc-table" aria-label="Consolidated picking list">
            <thead>
              <tr>
                <th style={{ width: "80px" }}>Bin</th>
                <th style={{ width: "48px" }}></th>
                <th>Item</th>
                <th>SKU</th>
                <th className="num" style={{ width: "70px" }}>
                  Qty
                </th>
              </tr>
            </thead>
            <tbody>
              {data.consolidated.map((r, i) => (
                <tr key={`${r.sku ?? r.title}-${i}`}>
                  <td>{r.bin ? <span className="doc-bin">{r.bin}</span> : <span className="doc-party__muted">—</span>}</td>
                  <td>
                    <Thumb src={r.imageUrl} alt={r.title} size={36} />
                  </td>
                  <td className="doc-cell-title">{r.title}</td>
                  <td className="doc-sku">{r.sku ?? "—"}</td>
                  <td className="num">
                    <span className="doc-qty-pill">{r.quantity}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="doc-table" aria-label="Per-order picking list">
            <thead>
              <tr>
                <th style={{ width: "48px" }}></th>
                <th>Item</th>
                <th>SKU</th>
                <th style={{ width: "80px" }}>Bin</th>
                <th>Per order</th>
                <th className="num" style={{ width: "80px" }}>
                  Required
                </th>
              </tr>
            </thead>
            <tbody>
              {data.perOrder.map((r, i) => (
                <tr key={`${r.sku ?? r.title}-${i}`}>
                  <td>
                    <Thumb src={r.imageUrl} alt={r.title} size={36} />
                  </td>
                  <td className="doc-cell-title">{r.title}</td>
                  <td className="doc-sku">{r.sku ?? "—"}</td>
                  <td>{r.bin ? <span className="doc-bin">{r.bin}</span> : <span className="doc-party__muted">—</span>}</td>
                  <td>
                    <div className="doc-perorder">
                      {r.perOrder.map((p, j) => (
                        <span className="doc-perorder__row" key={j}>
                          <span className="doc-perorder__order">{p.orderName}</span> × {p.quantity}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="num">
                    <span className="doc-qty-pill">{r.required}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="doc-required">
          <span>Total items required</span>
          <span className="mono">{data.totalItemsRequired}</span>
        </div>

        <footer className="doc-foot">
          <span>{data.org.website}</span>
          <span>{data.org.email}</span>
          <span>{data.org.phone}</span>
        </footer>
      </article>
    </PrintFrame>
  );
}
